import {
  useIncidentDetail,
  useIncidentNeighbourAlarms,
  useUpdateIncidentFields,
} from '@hakimo-ui/hakimo/data-access';
import { DataVersion, IncidentProduct } from '@hakimo-ui/hakimo/types';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { toast } from '@hakimo-ui/hakimo/util';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmailPayload } from './email-compose';
import { buildEmailFormData } from './email-utils';
import { IncidentDetailsBody } from './IncidentDetailsBody';
import { IncidentDetailsHeader } from './IncidentDetailsHeader';
import { IncidentDetailsModals } from './IncidentDetailsModals';
import { IncidentDetailsStates } from './IncidentDetailsStates';
import { useIncidentReportActions } from './useIncidentReportActions';
import { EditableField } from './util';

interface Props {
  alarmId: string;
  product: IncidentProduct;
  /** Scan always reads v2, so callers pass undefined for the scan product. */
  dataVersion?: DataVersion;
}

export function IncidentDetailsPage({ alarmId, product, dataVersion }: Props) {
  const navigate = useNavigate();
  const [showLogoModal, setShowLogoModal] = useState(false);

  const {
    data: incident,
    isLoading,
    isError,
    refetch,
  } = useIncidentDetail(alarmId, product, dataVersion);

  const { data: neighbours, isFetching: neighboursLoading } =
    useIncidentNeighbourAlarms(alarmId, product, dataVersion);

  const onSaveError = (err: Error) => {
    toast(err.message, { type: 'error' });
    refetch();
  };

  const actions = useIncidentReportActions(
    product,
    onSaveError,
    () => {
      toast('Report emailed');
      refetch();
    },
    dataVersion
  );

  const { mutate: updateFields, isLoading: saving } = useUpdateIncidentFields(
    product,
    () => {
      toast('Incident updated');
      actions.clearGeneratedReport();
      refetch();
    },
    onSaveError,
    dataVersion
  );

  const saveField = (
    field: EditableField,
    value: string | string[],
    onSaved: () => void
  ) =>
    incident &&
    updateFields(
      {
        alarm_id: incident.alarm_id,
        fields: { [field]: value },
      },
      { onSuccess: onSaved }
    );

  return (
    <Page
      title="Incident Details"
      onClickBack={() => navigate('/location-incidents')}
      titleFontSize="large"
      contentClassName=""
    >
      <IncidentDetailsStates
        isLoading={isLoading}
        isError={isError}
        hasIncident={!!incident}
        onRetry={() => refetch()}
      />

      {!isLoading && incident && (
        <>
          <IncidentDetailsHeader
            incident={incident}
            preparing={actions.preparing}
            sending={actions.sending}
            downloading={actions.downloading}
            onPreviewAndSend={() => actions.openPreview(incident.alarm_id)}
            onDownload={() =>
              actions.downloadReport({ alarm_id: incident.alarm_id })
            }
            onOpenLogoModal={() => setShowLogoModal(true)}
          />

          <IncidentDetailsBody
            incident={incident}
            product={product}
            neighbours={neighbours?.payload?.items ?? []}
            neighboursLoading={neighboursLoading}
            saving={saving}
            onSaveField={saveField}
            onTimelineChanged={actions.clearGeneratedReport}
            dataVersion={dataVersion}
          />

          <IncidentDetailsModals
            incident={incident}
            product={product}
            dataVersion={dataVersion}
            actions={actions}
            showLogoModal={showLogoModal}
            onCloseLogoModal={() => setShowLogoModal(false)}
            onSend={(payload: EmailPayload) =>
              actions.sendReport(buildEmailFormData(incident.alarm_id, payload))
            }
          />
        </>
      )}
    </Page>
  );
}

export default IncidentDetailsPage;
